// useIsAdmin — is the signed-in user a platform admin?
//
// Returns { isAdmin, loading, resolved }. `resolved` is the important one for
// any guard that REDIRECTS on the answer: it is true only when we have a
// definitive yes/no for the CURRENT user. Pending, errored, or mid-user-switch
// all report resolved=false, and a guard must do nothing in that state.
//
// ─── WHY THIS IS A REACT QUERY KEYED ON userId ──────────────────────────────
//
// The previous implementation was useState + useEffect, and it shipped a race
// that pinned a platform admin to a barn dashboard. Owner report 2026-08-18:
// signing in at raisedbeef.com landed on Beaver County's My Barn instead of
// Market Intelligence. PR #1855 had already fixed a DIFFERENT cause of the same
// symptom (stale location.state.from), which is why the symptom survived it.
//
// The race:
//
//   useUserProfile deliberately decouples `loading` from the session — it flips
//   loading to false as soon as it knows anything, and Supabase's
//   onAuthStateChange fires INITIAL_SESSION with session=null before the stored
//   session is restored (the 3s AUTH_RESOLVE_TIMEOUT_MS fallback does the same).
//   So `session` goes null → truthy while `loading` is ALREADY false.
//
//   The old effect ran once with no user and set loading=false / isAdmin=false.
//   When the session arrived it re-ran and started a fresh query — but nothing
//   ever set loading back to TRUE. That left a window where:
//
//       session = truthy,  isAdmin = false,  loading = false
//
//   …which reads exactly like "signed in, definitely not an admin."
//
//   BarnOnlyLock believed it, decided an admin who owns barns was a barn
//   account, and redirected to /my-barn — which renders barns[0], and
//   barns_for_user orders by name, hence Beaver County out of the four.
//
// Keying the query on userId removes the window by construction: when the user
// id changes, there is no cached data for the new key, so the hook reports
// pending instead of a stale `false`.
//
// ─── AND WHY THE ERROR IS NO LONGER SWALLOWED ───────────────────────────────
//
// The old code destructured `const { data } = await …`, discarding `error`, so
// any transient network blip or RLS change made a real admin look like a
// non-admin — with the same consequence. An error now leaves resolved=false so
// callers fail safe rather than acting on a fabricated "no".
//
// Reading own roles is permitted by RLS ("Users can read own roles":
// auth.uid() = user_id), so a failure here is genuinely exceptional.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

export interface IsAdminState {
  /** True only when the user is confirmed to hold the admin role. */
  isAdmin: boolean;
  /** True while we do not yet have an answer. */
  loading: boolean;
  /**
   * True only when `isAdmin` is a definitive answer for the CURRENT user.
   * False while pending, on error, and across a user switch.
   *
   * Any guard that redirects MUST check this. `isAdmin === false` on its own
   * means "not known to be an admin", which is not the same as "not an admin".
   */
  resolved: boolean;
}

export function useIsAdmin(): IsAdminState {
  const { session, loading: authLoading } = useUserProfile();
  const userId = session?.user.id ?? null;

  const query = useQuery<boolean>({
    // userId in the key is load-bearing — see the header.
    queryKey: ["is-admin", userId],
    enabled: !authLoading && !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId as string)
        .eq("role", "admin")
        .maybeSingle();
      // Thrown, not swallowed: an error must not read as "not an admin".
      if (error) throw error instanceof Error ? error : new Error(String(error));
      return !!data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Signed out is a definitive answer: not an admin, nothing pending.
  if (!authLoading && !userId) {
    return { isAdmin: false, loading: false, resolved: true };
  }

  if (authLoading) {
    return { isAdmin: false, loading: true, resolved: false };
  }

  if (query.isError) {
    // Unknown, not "no". resolved=false keeps redirecting guards inert.
    return { isAdmin: false, loading: false, resolved: false };
  }

  if (query.data === undefined) {
    return { isAdmin: false, loading: true, resolved: false };
  }

  return { isAdmin: query.data, loading: false, resolved: true };
}
